import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, TriangleAlert } from "lucide-react";
import { BrandBadge } from "@/components/BrandMark";

export default function AcceptInvite() {
  const { token } = useParams();
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/invitations/preview/${token}`)
      .then(({ data }) => {
        setInvite(data);
        setName(data.name || "");
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This invitation is not valid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await acceptInvite({ token, name, password: invite?.account_exists ? undefined : password });
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div
      className="min-h-screen bg-royal-deep sidebar-grain flex items-center justify-center p-6"
      data-testid="accept-invite-page"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 2rem)" }}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-7 sm:p-9 rise">
        <div className="flex items-center gap-3">
          <BrandBadge size="h-11 w-11" icon="h-8 w-8" />
          <div>
            <p className="font-display text-royal-deep text-lg font-semibold leading-none">Cornerstone Ops</p>
            <p className="text-[10px] tracking-[0.22em] uppercase text-gold mt-1">Property Operations</p>
          </div>
        </div>

        {error && !invite && (
          <div className="mt-7 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
            <TriangleAlert className="h-4 w-4 text-red-600 mt-0.5" />
            <p className="text-sm text-red-700" data-testid="invite-error">
              {error}
            </p>
          </div>
        )}

        {!invite && !error && <Loader2 className="mt-8 h-6 w-6 animate-spin text-royal" />}

        {invite && (
          <form onSubmit={submit} className="mt-7" data-testid="invite-form">
            <div className="flex items-center gap-2 text-gold">
              <MailCheck className="h-4 w-4" />
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold">You're invited</p>
            </div>
            <h1 className="font-display text-3xl font-bold text-royal-deep mt-2">{invite.organization}</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {invite.invited_by} invited <strong className="text-royal-deep">{invite.email}</strong> to join as{" "}
              <strong className="text-royal-deep">{invite.role_label}</strong>.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
                <Input
                  data-testid="invite-name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 h-11"
                />
              </div>
              {!invite.account_exists && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Choose a password</Label>
                  <Input
                    data-testid="invite-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 h-11"
                  />
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    At least 8 characters, with letters and numbers.
                  </p>
                </div>
              )}
              {invite.account_exists && (
                <p className="rounded-md border border-royal/15 bg-royal/5 p-3 text-xs text-royal">
                  You already have a Cornerstone Ops account — accepting adds this company to your organization
                  switcher.
                </p>
              )}
            </div>

            {error && (
              <p className="mt-4 text-sm text-destructive" data-testid="invite-submit-error">
                {error}
              </p>
            )}

            <Button
              data-testid="accept-invite-btn"
              type="submit"
              disabled={busy}
              className="mt-6 w-full h-12 bg-royal hover:bg-royal-deep text-white font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept & continue"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
